<script setup lang="ts">
  // "Open table by number" keypad for the floor-plan view (ported from -pos
  // PosFloorKeypad.vue on PosUi primitives). Exact-name matcher lives in
  // utils/floor-keypad.ts; popover stays open on a miss (warning toast).
  import { getCurrentInstance, nextTick, ref } from 'vue';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    findTableByKeypadQuery,
    type IKeypadTableCandidate,
  } from '@/utils/floor-keypad';
  import { useUi } from '@/composition/use-ui';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';

  const props = defineProps<{
    tables: IKeypadTableCandidate[];
  }>();

  const emit = defineEmits<{
    (e: 'select', tableId: number): void;
  }>();

  const ui = useUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const query = ref('');
  const inputRef = ref<InstanceType<typeof PosUiInput>>();
  const popoverRef = ref<InstanceType<typeof PosUiPopover>>();

  async function focusInput(): Promise<void> {
    await nextTick();
    const field = (inputRef.value?.$el as HTMLElement | undefined)?.querySelector(
      'input'
    );
    field?.focus();
  }

  function handleTriggerClick(open: boolean, toggle: () => void): void {
    if (!open) {
      query.value = '';
      void focusInput();
    }
    toggle();
  }

  function appendDigit(digit: string): void {
    query.value += digit;
    void focusInput();
  }

  function backspace(): void {
    query.value = query.value.slice(0, -1);
    void focusInput();
  }

  function submit(close: () => void): void {
    const q = query.value.trim();
    if (!q) return;

    const match = findTableByKeypadQuery(props.tables, q);
    if (!match) {
      ui.showToast(AlertType.Info, String(proxy.$t('posV2.tableNotFound')));
      void focusInput();
      return;
    }

    close();
    query.value = '';
    emit('select', match.id);
  }
</script>

<template>
  <PosUiPopover
    ref="popoverRef"
    placement="bottom"
    align="end"
    class="pos-v2-floor-keypad"
  >
    <template #trigger="{ open, toggle }">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="md"
        icon="dialpad"
        :aria-label="$t('posV2.keypadOpenByNumber')"
        @click="handleTriggerClick(open, toggle)"
      >
        {{ $t('posV2.keypadTrigger') }}
      </PosUiButton>
    </template>

    <template #default="{ close }">
      <div class="keypad-panel">
        <PosUiInput
          ref="inputRef"
          :value="query"
          size="lg"
          :placeholder="i18nT('posV2.keypadPlaceholder')"
          class="keypad-input"
          @input="(v) => (query = v)"
          @enter="submit(close)"
        />

        <div class="keypad-grid">
          <PosUiButton
            v-for="digit in ['1', '2', '3', '4', '5', '6', '7', '8', '9']"
            :key="digit"
            color="neutral"
            variant="soft"
            size="lg"
            class="keypad-key"
            @click="appendDigit(digit)"
          >
            {{ digit }}
          </PosUiButton>

          <PosUiButton
            color="neutral"
            variant="soft"
            size="lg"
            class="keypad-key"
            icon="backspace-outline"
            :aria-label="$t('posV2.keypadBackspace')"
            @click="backspace"
          />
          <PosUiButton
            color="neutral"
            variant="soft"
            size="lg"
            class="keypad-key"
            @click="appendDigit('0')"
          >
            0
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="solid"
            size="lg"
            class="keypad-key"
            icon="arrow-right"
            :aria-label="$t('posV2.keypadOpenTable')"
            @click="submit(close)"
          />
        </div>
      </div>
    </template>
  </PosUiPopover>
</template>

<style scoped lang="scss">
  .pos-v2-floor-keypad {
    // Fills the popover's fixed panel width (tw-w-72) for touch-sized keys.
    .keypad-panel {
      @apply tw-flex tw-w-full tw-flex-col tw-gap-2;

      .keypad-input {
        @apply tw-w-full;
      }

      .keypad-grid {
        @apply tw-grid tw-grid-cols-3 tw-gap-1.5;

        .keypad-key {
          @apply tw-min-h-12 tw-justify-center tw-text-lg tw-font-semibold;
        }
      }
    }
  }
</style>
